import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Box, Typography } from '@mui/material';

// Idempotent registration (same pattern as the other Chart.js pages). Local to this
// component so it cannot regress other chart usages.
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const DEFAULT_COLORS = {
  ink: '#17323c',
  muted: '#4f646f',
  success: '#2f7d5d',
  danger: '#9c4f4f',
  accent: '#c07c32',
};

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatUsdt(value) {
  const n = toNumber(value);
  return n === null
    ? '—'
    : `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT`;
}

function formatClosedAt(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

/**
 * Read-only chart of the general Admin Trading SHADOW realized net cumulative P&L.
 *
 * <p>Source of truth: the backend `GET /api/admin/trading/pnl-history` series, whose population is
 * the same authoritative market-closed SHADOW population as `portfolio.closedRealizedPnl`. The
 * plotted Y value is the backend `cumulativeNetRealizedPnl` used directly — the frontend NEVER
 * recomputes a running cumulative sum. Realized closes only; no unrealized/mark-to-market movement.
 *
 * @param {Object} props
 * @param {Array}  props.points          backend `points` (realized close sequence)
 * @param {boolean} props.available      backend `dataQuality.complete` (fail-closed)
 * @param {number} [props.closedPositions] authoritative closed population size (empty vs unavailable)
 * @param {Object} [props.colors]        optional page palette override
 */
export default function RealizedPnlChart({ points: rawPoints, available = false, closedPositions = 0, colors }) {
  const palette = { ...DEFAULT_COLORS, ...(colors || {}) };
  const points = Array.isArray(rawPoints) ? rawPoints : [];

  if (!available) {
    if (closedPositions > 0) {
      return (
        <Typography variant="body2" data-testid="realized-pnl-unavailable" sx={{ color: palette.muted, py: 2 }}>
          Courbe indisponible — données de clôture ou de P&L incomplètes.
        </Typography>
      );
    }
    return (
      <Typography variant="body2" data-testid="realized-pnl-empty" sx={{ color: palette.muted, py: 2 }}>
        Aucune position SHADOW clôturée à afficher.
      </Typography>
    );
  }

  if (points.length === 0) {
    return (
      <Typography variant="body2" data-testid="realized-pnl-empty" sx={{ color: palette.muted, py: 2 }}>
        Aucune position SHADOW clôturée à afficher.
      </Typography>
    );
  }

  const labels = points.map((p) => formatClosedAt(p.closedAt));
  // Authoritative cumulative values straight from the backend — no client-side accumulation.
  const cumulative = points.map((p) => toNumber(p.cumulativeNetRealizedPnl));
  const pointColors = cumulative.map((v) => (v !== null && v < 0 ? palette.danger : palette.success));

  return (
    <Box data-testid="realized-pnl-chart" sx={{ width: '100%', height: 280 }}>
      <Line
        data={{
          labels,
          datasets: [
            {
              label: 'P&L net réalisé cumulé (USDT)',
              data: cumulative,
              borderColor: palette.accent,
              backgroundColor: 'transparent',
              fill: false,
              tension: 0.2,
              pointRadius: 3,
              pointHoverRadius: 5,
              pointBackgroundColor: pointColors,
              pointBorderColor: pointColors,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const p = points[items?.[0]?.dataIndex];
                  return p ? formatClosedAt(p.closedAt) : '';
                },
                label: (ctx) => {
                  const p = points[ctx.dataIndex];
                  if (!p) return '';
                  return [
                    `${p.canonicalAsset || '—'} · ${p.direction || '—'} · ${p.pattern || '—'}${p.engineVersion ? ` (${p.engineVersion})` : ''}`,
                    `P&L net : ${formatUsdt(p.netRealizedPnl)}`,
                    `Cumulé : ${formatUsdt(p.cumulativeNetRealizedPnl)}`,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              ticks: { maxTicksLimit: 6, color: palette.muted },
              grid: { display: false },
            },
            y: {
              title: { display: true, text: 'P&L net réalisé cumulé (USDT)', color: palette.muted },
              ticks: { color: palette.muted },
              grid: {
                color: (ctx) => (ctx.tick && ctx.tick.value === 0 ? palette.ink : 'rgba(0,0,0,0.06)'),
                lineWidth: (ctx) => (ctx.tick && ctx.tick.value === 0 ? 2 : 1),
              },
            },
          },
        }}
      />
    </Box>
  );
}
